import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type { AdminCategory } from '../../api/types.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
  TextArea,
  TextInput,
} from '../../components/ui.js';

export function AdminCategoriesTab() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ code: '', name: '', description: '', sortOrder: '100' });

  const categories = useQuery({
    queryKey: ['admin', 'repair-categories'],
    queryFn: () =>
      apiRequest<{ categories: AdminCategory[] }>('/api/admin/repair-categories').then(
        (result) => result.categories,
      ),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'repair-categories'] });
    void queryClient.invalidateQueries({ queryKey: ['reference', 'repair-categories'] });
  };

  const create = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/repair-categories', {
        method: 'POST',
        body: {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          sortOrder: Number(form.sortOrder) || 100,
        },
      }),
    onSuccess: () => {
      setCreating(false);
      setForm({ code: '', name: '', description: '', sortOrder: '100' });
      invalidate();
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The category could not be created. Please try again.',
      );
    },
  });

  const setActive = useMutation({
    mutationFn: ({ categoryId, isActive }: { categoryId: string; isActive: boolean }) =>
      apiRequest(`/api/admin/repair-categories/${categoryId}`, {
        method: 'PATCH',
        body: { isActive },
      }),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        title="Repair categories"
        subtitle="Categories offered when a work order is submitted. Deactivating one keeps it on existing work orders but hides it from new requests."
        actions={
          <Button onClick={() => setCreating((previous) => !previous)}>
            {creating ? 'Cancel' : 'Add category'}
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {creating && (
        <Card title="Add a category" className="mb-4">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field
                label="Code"
                htmlFor="categoryCode"
                required
                hint="Uppercase letters, digits, underscores."
              >
                <TextInput
                  id="categoryCode"
                  value={form.code}
                  onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
                />
              </Field>
              <Field label="Name" htmlFor="categoryName" required>
                <TextInput
                  id="categoryName"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </Field>
              <Field label="Sort order" htmlFor="sortOrder">
                <TextInput
                  id="sortOrder"
                  inputMode="numeric"
                  value={form.sortOrder}
                  onChange={(event) => setForm({ ...form, sortOrder: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Description" htmlFor="categoryDescription">
              <TextArea
                id="categoryDescription"
                rows={2}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </Field>
            <div>
              <Button
                onClick={() => {
                  setError(null);
                  create.mutate();
                }}
                disabled={
                  !/^[A-Z0-9_]{2,40}$/.test(form.code.trim()) || form.name.trim().length < 2
                }
                loading={create.isPending}
              >
                Add category
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card bodyClassName="px-0 py-0 sm:px-0">
        {categories.isLoading ? (
          <div className="p-4">
            <Spinner label="Loading categories" />
          </div>
        ) : (categories.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No categories yet"
            description="Add the repair categories USPS should choose from."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Repair categories</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Code
                  </th>
                  <th scope="col" className="table-header-cell">
                    Name
                  </th>
                  <th scope="col" className="table-header-cell">
                    Description
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Work orders
                  </th>
                  <th scope="col" className="table-header-cell">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.data?.map((category) => (
                  <tr key={category.id} className={category.isActive ? undefined : 'bg-slate-50'}>
                    <td className="table-cell-base font-mono text-xs">{category.code}</td>
                    <td className="table-cell-base">
                      {category.name}
                      {!category.isActive && (
                        <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="table-cell-base">{category.description ?? '—'}</td>
                    <td className="table-cell-base text-right tabular-nums">
                      {category.workOrderCount}
                    </td>
                    <td className="table-cell-base">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setActive.mutate({
                            categoryId: category.id,
                            isActive: !category.isActive,
                          })
                        }
                      >
                        {category.isActive ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
